import * as LDClient from 'launchdarkly-js-client-sdk';
import AuthService from '@services/auth/auth.service';
import { useState, useEffect } from 'react';

export enum FeatureFlag {
  TOGGLE_SNAPSHOTS = 'toggle-snapshots',
  SYSTEM_SCHEDULER = 'system-scheduler',
  TENANT_ASSISTANT = 'tenant-assistant',
  PIVOT_TABLES = 'pivot-tables',
  CELL_ACTIONS = 'cell-actions',
  TEMPLATES_EDITOR = 'templates-editor',
  TEMPLATE_MANAGEMENT = 'template-management',
  TEMPLATE_UPGRADE = 'template-upgrade',
  HOSTED_SUPERSET = 'hosted-superset',
  PLIABLE_VISUALIZATIONS = 'pliable-visualizations',
  CUSTOMER_ACQUISITION_FORMAT = 'customer-acquisition-format',
  ENABLE_ELLIE_MODES = 'enable_ellie_modes',
  FORCE_NEXT = 'force-next',
}

type FlagChangeListener = (newValue: any) => void;

export default class FeatureFlagService {
  private static instance: FeatureFlagService;
  private client: LDClient.LDClient | null = null;
  private authService: AuthService | null = null;
  private cachedFlags: Map<string, any> = new Map();
  private listeners: Map<string, Set<FlagChangeListener>> = new Map();
  private _isReady: boolean = false;
  private readyPromise: Promise<void> | null = null;
  private authServiceUnsubscribe: (() => void) | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  // Initialize service with LaunchDarkly client from AuthService
  initialize(authService: AuthService) {
    this.authService = authService;
    
    // Create a promise that resolves when both AuthService and LaunchDarkly are ready
    this.readyPromise = new Promise((resolve) => {
      const checkReadiness = (state: any) => {
        console.log('AuthService state changed:', state);
        
        // Check if AuthService is authenticated and has ldClient
        if (state.isAuthenticated && authService.ldClient) {
          this.client = authService.ldClient;
          
          // Now wait for LaunchDarkly to initialize
          this.client.waitForInitialization().then(() => {
            console.log('LaunchDarkly initialized successfully');
            this._isReady = true;
            this.preloadFlags();
            
            // Set up change listener
            this.client!.on('change', (changes) => {
              console.log('LaunchDarkly flags changed:', changes);
              for (const flagKey in changes) {
                this.cachedFlags.set(flagKey, changes[flagKey].current);
                this.notifyListeners(flagKey, changes[flagKey].current);
              }
            });
            
            // Notify all existing listeners that the service is now ready
            // This will trigger them to re-evaluate their flags
            this.notifyAllListeners();
            
            // Unsubscribe from AuthService once we're ready
            if (this.authServiceUnsubscribe) {
              this.authServiceUnsubscribe();
              this.authServiceUnsubscribe = null;
            }
            
            resolve();
          }).catch((error) => {
            console.error('LaunchDarkly initialization failed:', error);
            this._isReady = false;
            resolve(); // Still resolve to prevent hanging
          });
        } else {
          console.log('AuthService not ready yet, waiting...', {
            isAuthenticated: state.isAuthenticated,
            hasLdClient: !!authService.ldClient
          });
        }
      };

      // Subscribe to AuthService state changes
      this.authServiceUnsubscribe = authService.subscribe(checkReadiness);
    });
  }

  private preloadFlags() {
    if (!this.client) return;
    
    Object.values(FeatureFlag).forEach(flagName => {
      try {
        const value = this.client!.variation(flagName, null);
        this.cachedFlags.set(flagName, value);
      } catch (error) {
        console.error(`Error preloading flag ${flagName}:`, error);
      }
    });
  }

  async waitForInitialization(): Promise<void> {
    if (this.readyPromise) {
      await this.readyPromise;
    }
  }

  // Clean up method to unsubscribe from AuthService if needed
  cleanup() {
    if (this.authServiceUnsubscribe) {
      this.authServiceUnsubscribe();
      this.authServiceUnsubscribe = null;
    }
  }

  getFlag<T>(name: FeatureFlag | string, defaultValue: T): T {
    // Always try to get from cache first
    if (this.cachedFlags.has(name)) {
      return this.cachedFlags.get(name) as T;
    }

    if (!this.client) {
      console.log(`No client available for flag ${name}, returning default:`, defaultValue);
      return defaultValue;
    }
    
    try {
      const value = this.client.variation(name, defaultValue);
      this.cachedFlags.set(name, value);
      console.log(`Got flag ${name} from client:`, value);
      return value;
    } catch (error) {
      console.error(`Error getting flag ${name}:`, error);
      return defaultValue;
    }
  }

  // Add a getter to expose isReady for debugging
  get isReady(): boolean {
    return this._isReady;
  }

  onFlagChange(name: FeatureFlag | string, callback: FlagChangeListener): () => void {
    if (!this.listeners.has(name)) {
      this.listeners.set(name, new Set());
    }
    
    this.listeners.get(name)!.add(callback);
    
    return () => {
      const flagListeners = this.listeners.get(name);
      if (flagListeners) {
        flagListeners.delete(callback);
      }
    };
  }

  private notifyListeners(name: string, newValue: any) {
    const flagListeners = this.listeners.get(name);
    if (flagListeners) {
      flagListeners.forEach(listener => {
        try {
          listener(newValue);
        } catch (error) {
          console.error(`Error in flag listener for ${name}:`, error);
        }
      });
    }
  }

  // Notify all listeners to re-evaluate their flags (used when service becomes ready)
  private notifyAllListeners() {
    console.log('Notifying all listeners that service is ready');
    this.listeners.forEach((flagListeners, flagName) => {
      const currentValue = this.getFlag(flagName, null);
      flagListeners.forEach(listener => {
        try {
          listener(currentValue);
        } catch (error) {
          console.error(`Error in flag listener for ${flagName}:`, error);
        }
      });
    });
  }

  isEnabled(name: FeatureFlag | string): boolean {
    return this.getFlag(name, false);
  }

  public static getInstance(): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService();
    }
    return FeatureFlagService.instance;
  }
}

// Hook with loading state
export function useFeatureFlag<T>(
  flagName: FeatureFlag | string, 
  defaultValue: T
): { value: T; isLoading: boolean } {
  const featureFlagService = FeatureFlagService.getInstance();
  const [value, setValue] = useState<T>(defaultValue);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;

    const updateFlag = () => {
      const flagValue = featureFlagService.getFlag(flagName, defaultValue);
      if (mounted) {
        setValue(flagValue);
      }
    };

    // Wait for initialization before setting the flag
    featureFlagService.waitForInitialization().then(() => {
      if (mounted) {
        updateFlag();
        setIsLoading(false);
      }
    });

    const unsubscribe = featureFlagService.onFlagChange(flagName, (newValue) => {
      if (mounted) {
        setValue(newValue as T);
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [flagName, defaultValue]);

  return { value, isLoading };
}

// Simple hook that returns just the value (starts with default, updates when ready)
export function useFeatureFlagValue<T>(flagName: FeatureFlag | string, defaultValue: T): T {
  const featureFlagService = FeatureFlagService.getInstance();
  const [value, setValue] = useState<T>(defaultValue);

  useEffect(() => {
    let mounted = true;

    const updateValue = () => {
      const flagValue = featureFlagService.getFlag(flagName, defaultValue);
      console.log(`Flag ${flagName}: ${flagValue} (isReady: ${featureFlagService.isReady})`);
      if (mounted) {
        setValue(flagValue);
      }
    };

    // Get current value immediately (might be cached or default)
    updateValue();

    // Set up listener for flag changes (this will be triggered when service becomes ready)
    const unsubscribe = featureFlagService.onFlagChange(flagName, (newValue) => {
      console.log(`Flag ${flagName} changed to:`, newValue);
      if (mounted) {
        setValue(newValue as T);
      }
    });

    // Also wait for initialization as a backup
    featureFlagService.waitForInitialization().then(() => {
      console.log(`LaunchDarkly ready, updating flag ${flagName}`);
      updateValue();
    }).catch((error) => {
      console.error(`Error waiting for LaunchDarkly initialization:`, error);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [flagName, defaultValue]);

  return value;
}